/** @shell standard @header bar */
import React, { useCallback, useMemo } from 'react';
import { View, Text, StyleSheet, Switch, Alert, type ViewStyle } from 'react-native';
import Animated from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Clickable2 } from '../components/common/Clickable2';
import { SvgIcon, type SvgIconName } from '../components/common/SvgIcon';
import { ScreenHeader, useHeaderTopPadding, useScreenHeaderScroll } from '../components/common/ScreenHeader';
import { appTheme, useThemeColors, useThemeStore, persistThemeAppearance, type ThemeColors } from '../shared/theme';
import { useTranslation } from '../shared/i18n';
import { useRtl } from '../shared/i18n/useRtl';
import { useRequireVerified } from '../shared/auth/useRequireVerified';
import { useAuthNavigation, useExitOrGoBack } from '../shared/navigation/useAuthNavigation';
import { HealthInformationCmsGate } from '../healthInformation/components/HealthInformationCmsGate';
import {
  StackScreenShell,
  studioSectionCardStyle,
  studioSectionLabelStyle,
  STACK_SCROLL_GUTTER,
  STACK_SECTION_GAP,
  androidScaleFont,
  androidScaleSize,
  androidFlatSurfaceStyle,
  DASH_STUDIO_INNER,
} from '../shared/theme/studio/stackScreenShell';

const ROW_ICON_SHELL = 40;
const ROW_ICON_GLYPH = 20;
const ROW_MIN_HEIGHT = 52;

function createSettingsStyles(c: ThemeColors) {
  return StyleSheet.create({
    content: {
      flex: 1,
    },
    /** Hit target + padding/border live on Clickable2 (or plain View for static rows). */
    settingItem: {
      minHeight: androidScaleSize(ROW_MIN_HEIGHT),
      paddingHorizontal: DASH_STUDIO_INNER,
      paddingVertical: appTheme.spacing.sm,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: c.borderLight,
      width: '100%',
      alignSelf: 'stretch',
    },
    settingItemLast: {
      borderBottomWidth: 0,
    },
    /** Actual LTR/RTL row — must be an inner View so flex children get a real width. */
    settingRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      gap: appTheme.spacing.md,
    },
    settingLeft: {
      flexDirection: 'row',
      alignItems: 'center',
      flexGrow: 1,
      flexShrink: 1,
      flexBasis: 0,
      minWidth: 0,
      gap: appTheme.spacing.md,
    },
    settingIconShell: {
      width: androidScaleSize(ROW_ICON_SHELL),
      height: androidScaleSize(ROW_ICON_SHELL),
      borderRadius: 13,
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      backgroundColor: c.primaryUltra,
      borderWidth: 1,
      borderColor: c.primaryFade,
      ...androidFlatSurfaceStyle(),
    },
    settingLabel: {
      ...appTheme.typography.bodyMedium,
      fontSize: androidScaleFont(15),
      fontWeight: '600',
      color: c.text,
      flexGrow: 1,
      flexShrink: 1,
      minWidth: 0,
    },
    settingRight: {
      flexDirection: 'row',
      alignItems: 'center',
      flexGrow: 0,
      flexShrink: 0,
      gap: appTheme.spacing.xs,
    },
    settingValue: {
      ...appTheme.typography.bodySmall,
      fontSize: androidScaleFont(14),
      color: c.textSecondary,
      fontWeight: '500',
    },
  });
}

type SettingRowChromeProps = {
  icon: SvgIconName;
  label: string;
  value?: string;
  disclosure_icon?: 'arrow-right' | 'arrow-left';
  styles: ReturnType<typeof createSettingsStyles>;
  colors: ThemeColors;
  flip_row_style: ViewStyle | undefined;
  text_align: 'left' | 'right';
  writing_direction: 'ltr' | 'rtl';
  muted_icon?: boolean;
};

function SettingRowChrome({
  icon,
  label,
  value,
  disclosure_icon,
  styles,
  colors,
  flip_row_style,
  text_align,
  writing_direction,
  muted_icon,
}: SettingRowChromeProps) {
  return (
    <View style={[styles.settingRow, flip_row_style]}>
      <View style={[styles.settingLeft, flip_row_style]}>
        <View
          style={[
            styles.settingIconShell,
            muted_icon
              ? { backgroundColor: colors.surface, borderColor: colors.borderLight }
              : null,
          ]}>
          <SvgIcon
            name={icon}
            size={ROW_ICON_GLYPH}
            color={muted_icon ? colors.textLight : colors.primary}
          />
        </View>
        <Text
          style={[styles.settingLabel, { textAlign: text_align, writingDirection: writing_direction }]}
          numberOfLines={2}>
          {label}
        </Text>
      </View>
      <View style={[styles.settingRight, flip_row_style]}>
        {value ? (
          <Text style={[styles.settingValue, { writingDirection: writing_direction }]} numberOfLines={1}>
            {value}
          </Text>
        ) : null}
        {disclosure_icon ? <SvgIcon name={disclosure_icon} size={18} color={colors.textLight} /> : null}
      </View>
    </View>
  );
}

type SettingNavRowProps = SettingRowChromeProps & {
  onPress: () => void;
  isLast?: boolean;
};

function SettingNavRow({ onPress, isLast, ...chrome }: SettingNavRowProps) {
  const { styles, label } = chrome;
  return (
    <Clickable2
      style={[styles.settingItem, isLast ? styles.settingItemLast : null]}
      onPress={onPress}
      haptics
      hitSlop={appTheme.hitSlop?.medium ?? 12}
      accessibilityLabel={label}
      accessibilityRole="button">
      <SettingRowChrome {...chrome} />
    </Clickable2>
  );
}

export const SettingsScreen: React.FC = () => {
  const navigation = useAuthNavigation();
  const on_back_exit = useExitOrGoBack();
  const insets = useSafeAreaInsets();
  const { t, locale, setLocale, getLocaleDisplayName } = useTranslation();
  const rtl = useRtl();
  const { flipRowStyle, textAlign, writingDirection, trailingRowDisclosureChevron } = rtl;
  useRequireVerified('Settings');
  const header_top_padding = useHeaderTopPadding();
  const { scrollY, onScroll } = useScreenHeaderScroll();
  const themeColors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createSettingsStyles(themeColors), [themeColors]);
  const section_card_style = useMemo(() => studioSectionCardStyle(themeColors), [themeColors]);
  const section_label_style = useMemo(
    () => [studioSectionLabelStyle(themeColors), { textAlign, writingDirection }],
    [themeColors, textAlign, writingDirection],
  );
  const appearance = useThemeStore(s => s.appearance);
  const is_dark_mode = appearance === 'dark';

  const showLanguagePicker = useCallback(() => {
    Alert.alert(t('settings.language'), t('settings.appLanguage'), [
      { text: getLocaleDisplayName('en'), onPress: () => setLocale('en') },
      { text: getLocaleDisplayName('ar'), onPress: () => setLocale('ar') },
      { text: t('common.cancel'), style: 'cancel' },
    ]);
  }, [t, setLocale, getLocaleDisplayName]);

  const nav = navigation as { navigate: (name: string) => void };
  const goTerms = useCallback(() => nav.navigate('TermsAndConditions'), [nav]);
  const goPrivacy = useCallback(() => nav.navigate('TermsAndConditions'), [nav]);
  const goAboutUs = useCallback(() => nav.navigate('AboutUs'), [nav]);
  const goHealthInformationSources = useCallback(() => nav.navigate('HealthInformationSources'), [nav]);
  const goNews = useCallback(() => nav.navigate('News'), [nav]);
  const disclosure_icon = trailingRowDisclosureChevron;

  const on_dark_mode_change = useCallback((dark: boolean) => {
    void persistThemeAppearance(dark ? 'dark' : 'light');
  }, []);

  const row_chrome = {
    styles,
    colors: themeColors,
    flip_row_style: flipRowStyle,
    text_align: textAlign,
    writing_direction: writingDirection,
  } as const;

  return (
    <StackScreenShell colors={themeColors}>
      <ScreenHeader title={t('settings.title')} onBack={on_back_exit} scrollY={scrollY} />

      <Animated.ScrollView
        style={styles.content}
        onScroll={onScroll}
        scrollEventThrottle={16}
        contentContainerStyle={{
          paddingHorizontal: STACK_SCROLL_GUTTER,
          gap: STACK_SECTION_GAP,
          paddingTop: header_top_padding,
          paddingBottom: insets.bottom + 24,
        }}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}>
        <View>
          <Text style={section_label_style}>{t('settings.language')}</Text>
          <View style={section_card_style}>
            <SettingNavRow
              icon="language"
              label={t('settings.appLanguage')}
              value={getLocaleDisplayName(locale)}
              onPress={showLanguagePicker}
              isLast
              disclosure_icon={disclosure_icon}
              {...row_chrome}
            />
          </View>
        </View>

        <View>
          <Text style={section_label_style}>{t('settings.appearance')}</Text>
          <View style={section_card_style}>
            <View style={[styles.settingItem, styles.settingItemLast]}>
              <View style={[styles.settingRow, flipRowStyle]}>
                <View style={[styles.settingLeft, flipRowStyle]}>
                  <View style={styles.settingIconShell}>
                    <SvgIcon name="sunny" size={ROW_ICON_GLYPH} color={themeColors.primary} />
                  </View>
                  <Text
                    style={[styles.settingLabel, { textAlign, writingDirection }]}
                    numberOfLines={2}>
                    {t('settings.darkMode')}
                  </Text>
                </View>
                <Switch
                  value={is_dark_mode}
                  onValueChange={on_dark_mode_change}
                  trackColor={{
                    false: themeColors.border,
                    true: themeColors.primary,
                  }}
                  accessibilityLabel={t('settings.darkMode')}
                />
              </View>
            </View>
          </View>
        </View>

        <View>
          <Text style={section_label_style}>{t('settings.about')}</Text>
          <View style={section_card_style}>
            <SettingNavRow
              icon="doc"
              label={t('settings.termsAndConditions')}
              onPress={goTerms}
              disclosure_icon={disclosure_icon}
              {...row_chrome}
            />
            <SettingNavRow
              icon="lock"
              label={t('settings.privacyPolicy')}
              onPress={goPrivacy}
              disclosure_icon={disclosure_icon}
              {...row_chrome}
            />
            <SettingNavRow
              icon="user"
              label={t('account.aboutUs')}
              onPress={goAboutUs}
              disclosure_icon={disclosure_icon}
              {...row_chrome}
            />
            <HealthInformationCmsGate>
              <SettingNavRow
                icon="medkit"
                label={t('settings.healthInformationSources')}
                onPress={goHealthInformationSources}
                disclosure_icon={disclosure_icon}
                {...row_chrome}
              />
            </HealthInformationCmsGate>
            <SettingNavRow
              icon="news"
              label={t('news.title')}
              onPress={goNews}
              disclosure_icon={disclosure_icon}
              {...row_chrome}
            />
            <View style={[styles.settingItem, styles.settingItemLast]}>
              <SettingRowChrome
                icon="package"
                label={t('settings.appVersion')}
                value="1.0.0"
                muted_icon
                {...row_chrome}
              />
            </View>
          </View>
        </View>
      </Animated.ScrollView>
    </StackScreenShell>
  );
};
